import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { motion } from "framer-motion";
import {
  ChartBar,
  ChartPie,
  TrendingUp,
  MessageSquare,
  DollarSign,
  Target,
  AlertCircle,
} from "lucide-react";
import type { PricingFeedbackAnalytics, Response } from "@/lib/campaignsApi";

// Example data types
interface SentimentData {
  name: string;
  value: number;
  color: string;
}

interface FeedbackItem {
  id: string;
  text: string;
  count: number;
  sentiment: "positive" | "neutral" | "negative";
}

interface CampaignAnalyticsProps {
  responseCount: number;
  sentimentData: SentimentData[];
  feedbackItems: FeedbackItem[];
  priceData: { label: string; count: number }[];
  pricingAnalytics?: PricingFeedbackAnalytics;
  responseAnalytics?: {
    totalResponses: number;
    responsesByQuestion: Array<{
      questionId: string;
      questionText: string;
      questionType: string;
      responseCount: number;
      responses: Response[];
    }>;
    completionRate: number;
  };
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const CampaignAnalytics = ({
  responseCount,
  sentimentData,
  feedbackItems,
  priceData,
  pricingAnalytics,
  responseAnalytics,
}: CampaignAnalyticsProps) => {
  // Calculate market validation score
  const marketValidationScore = useMemo(() => {
    if (!pricingAnalytics) return 0;

    const totalFeedback = pricingAnalytics.totalFeedback;
    if (totalFeedback === 0) return 0;

    const loveCount =
      pricingAnalytics.sentimentDistribution?.find(
        (s) => s.sentiment === "LOVE"
      )?._count.sentiment || 0;
    const likeCount =
      pricingAnalytics.sentimentDistribution?.find(
        (s) => s.sentiment === "LIKE"
      )?._count.sentiment || 0;
    const expensiveCount =
      pricingAnalytics.sentimentDistribution?.find(
        (s) => s.sentiment === "EXPENSIVE"
      )?._count.sentiment || 0;
    const notInterestedCount =
      pricingAnalytics.sentimentDistribution?.find(
        (s) => s.sentiment === "NOT_INTERESTED"
      )?._count.sentiment || 0;

    // Calculate score: (love + like) / total * 100
    const positiveSentiment = loveCount + likeCount;
    const negativeSentiment = expensiveCount + notInterestedCount;

    if (positiveSentiment + negativeSentiment === 0) return 0;

    return Math.round(
      (positiveSentiment / (positiveSentiment + negativeSentiment)) * 100
    );
  }, [pricingAnalytics]);

  // Calculate pricing confidence
  const pricingConfidence = useMemo(() => {
    if (!pricingAnalytics?.willingnessToPayStats) return 0;

    const stats = pricingAnalytics.willingnessToPayStats;
    const count = stats._count.willingnessToPay;
    const avg = stats._avg.willingnessToPay;
    const min = stats._min.willingnessToPay;
    const max = stats._max.willingnessToPay;

    if (count === 0 || !avg || !min || !max) return 0;

    // Calculate confidence based on data consistency
    const range = max - min;
    const coefficientOfVariation = range / avg;

    // Higher confidence for more consistent data
    return Math.max(0, Math.round((1 - coefficientOfVariation) * 100));
  }, [pricingAnalytics]);

  // Get most common price point
  const mostCommonPrice =
    priceData.length > 0
      ? priceData.reduce((prev, current) =>
          prev.count > current.count ? prev : current
        ).label
      : "No data";

  // Get average willingness to pay
  const averageWillingnessToPay =
    pricingAnalytics?.willingnessToPayStats?._avg.willingnessToPay || 0;

  // Check if we have any data
  const hasData =
    responseCount > 0 ||
    sentimentData.some((s) => s.value > 0) ||
    feedbackItems.length > 0 ||
    priceData.some((p) => p.count > 0) ||
    (pricingAnalytics && pricingAnalytics.totalFeedback > 0);

  if (!hasData) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ChartBar className="w-10 h-10 text-primary" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-3">
            No Analytics Data Yet
          </h3>
          <p className="text-slate-600 max-w-md mx-auto mb-6 leading-relaxed">
            Analytics will appear here once your campaign receives feedback,
            comments, and pricing responses from users.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Target className="w-4 h-4" />
              <span>Share your campaign to collect data</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Enhanced Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                Total Responses
              </CardDescription>
              <CardTitle className="text-3xl text-blue-900">
                {responseCount}
              </CardTitle>
            </CardHeader>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-emerald-100">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                Market Validation
              </CardDescription>
              <CardTitle className="text-3xl text-emerald-900 flex items-center">
                {marketValidationScore}%
                <span className="text-lg text-emerald-700 ml-2">
                  {marketValidationScore >= 70
                    ? "Strong"
                    : marketValidationScore >= 50
                    ? "Moderate"
                    : "Needs Work"}
                </span>
              </CardTitle>
            </CardHeader>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-purple-600" />
                Preferred Price
              </CardDescription>
              <CardTitle className="text-3xl text-purple-900">
                {mostCommonPrice}
              </CardTitle>
            </CardHeader>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-amber-100">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-600" />
                Pricing Confidence
              </CardDescription>
              <CardTitle className="text-3xl text-amber-900">
                {pricingConfidence}%
              </CardTitle>
            </CardHeader>
          </Card>
        </motion.div>
      </div>

      {/* Pricing Insights Section */}
      {pricingAnalytics && pricingAnalytics.totalFeedback > 0 && (
        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50">
              <CardTitle className="flex items-center text-slate-900">
                <DollarSign className="mr-2 size-5 text-emerald-600" />
                Pricing Insights
              </CardTitle>
              <CardDescription>
                Key insights from your pricing feedback
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900 mb-2">
                    ${averageWillingnessToPay.toFixed(0)}
                  </div>
                  <div className="text-sm text-slate-600">
                    Average Willingness to Pay
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900 mb-2">
                    {pricingAnalytics.willingnessToPayStats?._count
                      .willingnessToPay || 0}
                  </div>
                  <div className="text-sm text-slate-600">
                    Users Provided Price Feedback
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900 mb-2">
                    {pricingAnalytics.totalFeedback}
                  </div>
                  <div className="text-sm text-slate-600">
                    Total Pricing Responses
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Response Analytics Section */}
      {responseAnalytics && responseAnalytics.totalResponses > 0 && (
        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50">
              <CardTitle className="flex items-center text-slate-900">
                <MessageSquare className="mr-2 size-5 text-blue-600" />
                Response Analytics
              </CardTitle>
              <CardDescription>
                Insights from your campaign questions
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900 mb-2">
                    {responseAnalytics.totalResponses}
                  </div>
                  <div className="text-sm text-slate-600">
                    Total Question Responses
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900 mb-2">
                    {responseAnalytics.completionRate}%
                  </div>
                  <div className="text-sm text-slate-600">Completion Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900 mb-2">
                    {responseAnalytics.responsesByQuestion.length}
                  </div>
                  <div className="text-sm text-slate-600">
                    Questions Answered
                  </div>
                </div>
              </div>

              {/* Question Response Breakdown */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900 mb-3">
                  Question Response Breakdown
                </h4>
                {responseAnalytics.responsesByQuestion.map((question) => (
                  <div
                    key={question.questionId}
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {question.questionText.length > 60
                            ? question.questionText.substring(0, 60) + "..."
                            : question.questionText}
                        </span>
                        <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded-full capitalize">
                          {question.questionType.toLowerCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-slate-900">
                        {question.responseCount} responses
                      </span>
                    </div>
                    <div className="text-xs text-slate-600">
                      Response rate:{" "}
                      {Math.round(
                        (question.responseCount /
                          responseAnalytics.totalResponses) *
                          100
                      )}
                      %
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-slate-50 to-blue-50">
              <div>
                <CardTitle className="flex items-center text-slate-900">
                  <ChartPie className="mr-2 size-5 text-blue-600" />
                  Pricing Sentiment
                </CardTitle>
                <CardDescription>
                  How users feel about your pricing
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {sentimentData.some((s) => s.value > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={sentimentData.filter((s) => s.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {sentimentData
                        .filter((s) => s.value > 0)
                        .map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <AlertCircle className="w-12 h-12 text-slate-400 mb-4" />
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">
                    No Pricing Sentiment Data
                  </h4>
                  <p className="text-slate-600">
                    Pricing sentiment will appear when users provide pricing
                    feedback.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-slate-50 to-emerald-50">
              <div>
                <CardTitle className="flex items-center text-slate-900">
                  <ChartBar className="mr-2 size-5 text-emerald-600" />
                  Price Sensitivity
                </CardTitle>
                <CardDescription>
                  What users would pay for your product
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {priceData.some((p) => p.count > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={priceData.filter((p) => p.count > 0)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <DollarSign className="w-12 h-12 text-slate-400 mb-4" />
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">
                    No Pricing Data
                  </h4>
                  <p className="text-slate-600">
                    Pricing insights will appear when users provide pricing
                    feedback.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Enhanced Key Insights */}
      <motion.div variants={itemVariants}>
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-amber-50">
            <CardTitle className="flex items-center text-slate-900">
              <Target className="mr-2 size-5 text-amber-600" />
              Key Insights & Recommendations
            </CardTitle>
            <CardDescription>
              Actionable insights from your campaign data
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {feedbackItems.length > 0 ? (
              <div className="space-y-4">
                {feedbackItems.map((item) => (
                  <div
                    key={item.id}
                    className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                      item.sentiment === "positive"
                        ? "border-green-200 bg-green-50 hover:bg-green-100"
                        : item.sentiment === "negative"
                        ? "border-red-200 bg-red-50 hover:bg-red-100"
                        : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm text-slate-900">
                        {item.sentiment === "positive"
                          ? "✅"
                          : item.sentiment === "negative"
                          ? "❌"
                          : "📝"}{" "}
                        {item.text}
                      </span>
                      <span
                        className={`text-xs py-1 px-3 rounded-full font-medium ${
                          item.sentiment === "positive"
                            ? "bg-green-200 text-green-800"
                            : item.sentiment === "negative"
                            ? "bg-red-200 text-red-800"
                            : "bg-slate-200 text-slate-800"
                        }`}
                      >
                        {item.count} {item.count > 1 ? "mentions" : "mention"}
                      </span>
                    </div>
                    <div
                      className={`text-xs ${
                        item.sentiment === "positive"
                          ? "text-green-700"
                          : item.sentiment === "negative"
                          ? "text-red-700"
                          : "text-slate-600"
                      }`}
                    >
                      {item.sentiment === "positive"
                        ? "Positive feedback - users like this aspect"
                        : item.sentiment === "negative"
                        ? "Area for improvement - consider addressing this"
                        : "Neutral feedback - could be enhanced"}
                    </div>
                  </div>
                ))}

                {/* Market Validation Summary */}
                {marketValidationScore > 0 && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      Market Validation Summary
                    </h4>
                    <div className="space-y-2 text-sm text-blue-800">
                      <p>
                        • <strong>{marketValidationScore}%</strong> of users
                        have positive sentiment about your product
                      </p>
                      {marketValidationScore >= 70 && (
                        <p>
                          • <strong>Strong market validation</strong> - consider
                          proceeding with development
                        </p>
                      )}
                      {marketValidationScore >= 50 &&
                        marketValidationScore < 70 && (
                          <p>
                            • <strong>Moderate validation</strong> - consider
                            refining your value proposition
                          </p>
                        )}
                      {marketValidationScore < 50 && (
                        <p>
                          • <strong>Needs improvement</strong> - focus on
                          addressing user concerns
                        </p>
                      )}
                      {averageWillingnessToPay > 0 && (
                        <p>
                          • Users are willing to pay an average of{" "}
                          <strong>${averageWillingnessToPay.toFixed(0)}</strong>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-400 mb-4" />
                <h4 className="text-lg font-semibold text-slate-900 mb-2">
                  No Feedback Insights Yet
                </h4>
                <p className="text-slate-600">
                  Key insights will appear when users provide detailed feedback
                  on your campaign.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default CampaignAnalytics;
